<template>
  <div class="q-ma-md">
    <div style="display: flex; flex-wrap: wrap;">
      <q-input
        style="width: 200px"
        label="Unit Fitness:"
        borderless
        readonly
      />
      <q-input
        v-model="algorithmUnitResult.unitFitness"
        readonly
      />
    </div>
    <div style="display: flex; flex-wrap: wrap;">
      <q-input
        style="width: 200px"
        label="Unit Room Rate Tables:"
        borderless
        readonly
      />
      <q-input
        v-model="unitAvailableRoomRateTables"
        readonly
        style="width: 800px"
      />
    </div>
    <div style="display: flex; flex-wrap: wrap;">
      <q-input
        style="width: 200px"
        label="Gender Compatibility:"
        borderless
        readonly
      />
      <q-input
        v-model="algorithmUnitResult.unitGenderCompatibility"
        readonly
        style="width: 800px"
      />
    </div>
    <div style="width: 1100px">
      <q-table
        grid
        title="Applicants"
        :rows="algorithmUnitResult.algorithmApplicants"
        :columns="columns"
        row-key="name"
        hide-header
        hide-pagination
        ref="applicantTableRef"
      >
       <template v-slot:item="props">
       <!-- col-xs-12 col-sm-6 col-md-4 col-lg-3 -->
        <div class="q-table__grid-item" style="width:350px;">
          <div class="q-table__grid-item-card q-table__card">
            <div
              class="q-table__grid-item-row"
              v-for="col in props.cols" :key="col.name"
            >
              <q-separator v-if="col.name === 'preferSeparator'"/>
              <div v-else>
                <div class="q-table__grid-item-title">
                  {{ col.label }}
                </div>
                <div
                  v-if="col.name === 'preferredRoommateGenders'"
                >
                  <!-- eslint-disable max-len -->
                  <div
                    :style="{
                      background: `linear-gradient(90deg, rgba(0, 139, 0, 0.3) ${getPercentage(props.row, col.field)}%, rgba(139, 0, 0, 0.3) ${1 - getPercentage(props.row, col.field)}%)`,
                      padding: '2px'
                    }"
                  />
                  <div
                    v-for="gender in col.value" :key="gender"
                    class="q-table__grid-item-value"
                  >
                    {{ gender }}
                  </div>
                </div>
                <div
                  v-else-if="col.name.startsWith('prefer')"
                  class="q-table__grid-item-value"
                  >
                  {{ formatRows(col.value) }}
                  <!-- eslint-disable max-len -->
                  <div
                    :style="{
                      background: `linear-gradient(90deg, rgba(0, 139, 0, 0.3) ${getPercentage(props.row, col.field)}%, rgba(139, 0, 0, 0.3) ${1 - getPercentage(props.row, col.field)}%)`,
                      padding: '2px'
                    }"
                  />
                  <!-- <div
                    style="
                      background: linear-gradient(90deg, rgba(0, 139, 0, 0.3) 50%, rgba(139, 0, 0, 0.3) 50%);
                      padding: 1px;
                    "
                  /> -->
                </div>
                <div v-else class="q-table__grid-item-value">
                  {{ formatRows(col.value) }}
                  <div v-if="col.name === 'degree' || col.name === 'schoolYear'"
                    :style="{
                      background: `linear-gradient(90deg, rgba(0, 139, 0, 0.3) ${getPercentage(props.row, col.field)}%, rgba(139, 0, 0, 0.3) ${1 - getPercentage(props.row, col.field)}%)`,
                      padding: '2px'
                    }"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </q-table>
    </div>
  </div>
</template>

<script setup>
import {
  ref,
  onActivated,
  defineProps,
  onMounted,
  computed,
} from 'vue';
import { date, useQuasar } from 'quasar';
import usePlacement from '@/hooks/placement';
import useAlgorithmOptions from '@/hooks/algorithmOptions';

const props = defineProps({
  sessionId: Number,
});

const columns = [
  { name: 'fitness', label: 'Fitness', field: 'fitness' },
  { name: 'fullName', label: 'Full Name', field: 'fullName' },
  { name: 'digiPenEmail', label: 'DigiPen Email', field: 'digiPenEmail' },
  { name: 'gender', label: 'Gender', field: 'gender' },
  {
    name: 'birthday', label: 'Birthday', field: 'birthday', format: (val) => date.formatDate(val, 'MM/DD/YYYY'),
  },
  { name: 'isClean', label: 'Is Clean', field: 'isClean' },
  { name: 'isSocial', label: 'Is Social', field: 'isSocial' },
  { name: 'isSharing', label: 'Is Sharing', field: 'isSharing' },
  { name: 'isMorningPerson', label: 'Is Morning Person', field: 'isMorningPerson' },
  { name: 'isNightPerson', label: 'Is Night Person', field: 'isNightPerson' },
  { name: 'roommatesHaveEmotionalSupportAnimal', label: 'Is ok Living with ESA', field: 'roommatesHaveEmotionalSupportAnimal' },
  { name: 'animalInPlacement', label: 'Has ESA', field: 'animalInPlacement' },
  { name: 'hasPetAllergies', label: 'Has Pet Allergies', field: 'hasPetAllergies' },
  { name: 'degree', label: 'Degree', field: 'degree' },
  { name: 'schoolYear', label: 'School Year', field: 'schoolYear' },
  { name: 'preferSeparator' },
  { name: 'preferRoommateClean', label: 'Prefer Roommate Clean', field: 'preferRoommateClean' },
  { name: 'preferRoommateSocial', label: 'Prefer Roommate Social', field: 'preferRoommateSocial' },
  { name: 'preferRoommateSharing', label: 'Prefer Roommate Sharing', field: 'preferRoommateSharing' },
  { name: 'preferRoommateSameDegree', label: 'Prefer Roommate Same Degree', field: 'preferRoommateSameDegree' },
  { name: 'preferredApartmentRoomRateTable1', label: 'Prefer Apartment Room Rate 1', field: 'preferredApartmentRoomRateTable1' },
  { name: 'preferredApartmentRoomRateTable2', label: 'Prefer Apartment Room Rate 2', field: 'preferredApartmentRoomRateTable2' },
  { name: 'preferredRommatesDigiPenEmails0', label: 'Prefer Roommate 1', field: 'preferredRommatesDigiPenEmails0' },
  { name: 'preferredRommatesDigiPenEmails1', label: 'Prefer Roommate 2', field: 'preferredRommatesDigiPenEmails1' },
  { name: 'preferredRommatesDigiPenEmails2', label: 'Prefer Roommate 3', field: 'preferredRommatesDigiPenEmails2' },
  { name: 'preferredRommatesDigiPenEmails3', label: 'Prefer Roommate 4', field: 'preferredRommatesDigiPenEmails3' },
  { name: 'preferredRoommateGenders', label: 'Prefer Genders', field: 'preferredRoommateGenders' },
];
const $q = useQuasar();
const {
  algorithmWeights,
  buyoutRoomRates,
  iterationCount,
  raRoomRate,
} = useAlgorithmOptions();
const { currentUnitPlacement, getAlgorithmUnitResult } = usePlacement();
const algorithmUnitResult = ref({ algorithmApplicants: [] });
const applicantTableRef = ref(null);

onMounted(() => {
  const tableContent = applicantTableRef.value?.$el?.querySelector('.q-table__grid-content');
  if (tableContent) {
    tableContent.style.flexWrap = 'nowrap';
  }
});
// const gradientStart = [0, 55, 237];
// const gradientEnd = [180, 197, 241];
// const computeColor = (val) => {
//   const color = [];
//   for (let i = 0; i < gradientStart.length; i += 1) {
//     color.push(Math.floor(val * gradientStart[i] + (1 - val) * gradientEnd[i]));
//   }
//   return `rgb(${color[0]}, ${color[1]}, ${color[2]})`;
// };

onActivated(async () => {
  const algorithmOptions = {
    weights: algorithmWeights.value,
    buyoutRoomRates: buyoutRoomRates.value,
    iterationCount: iterationCount.value,
    raRoomRate: raRoomRate.value,
  };

  const userIds = currentUnitPlacement.value.placementModels
    .map((x) => x.userId).filter((x) => x !== null);

  const auditParameters = {
    sessionId: props.sessionId,
    userIds,
    unitId: currentUnitPlacement.value.unitId,
    algorithmOptions,
  };
  $q.loading.show();
  algorithmUnitResult.value = await getAlgorithmUnitResult(auditParameters);

  const { algorithmApplicants } = algorithmUnitResult.value;
  for (let i = 0; i < algorithmApplicants.length; i += 1) {
    for (let j = 0; j < algorithmApplicants[i].preferredRommatesDigiPenEmails.length; j += 1) {
      algorithmApplicants[i][`preferredRommatesDigiPenEmails${j}`] = algorithmApplicants[i].preferredRommatesDigiPenEmails[j];
      algorithmApplicants[i].algorithmApplicantMatching[`preferredRommatesDigiPenEmails${j}`] = algorithmApplicants[i].algorithmApplicantMatching.preferredRommatesDigiPenEmails[j];
    }
  }
  $q.loading.hide();
});

const unitAvailableRoomRateTables = computed(() => {
  if (algorithmUnitResult.value.unitAvailableRoomRateTables) {
    return algorithmUnitResult.value.unitAvailableRoomRateTables.join(', ');
  }
  return '';
});

const getPercentage = (row, col) => {
  const count = row.algorithmApplicantMatching[col];
  if (count === undefined) {
    return '0%';
  }
  if (col.startsWith('preferredApartmentRoomRateTable') || col.startsWith('preferredRommatesDigiPenEmails')) {
    return count * 100;
  }
  const total = algorithmUnitResult.value.algorithmApplicants.length;
  const percentage = Math.ceil((count / (total - 1)) * 100);
  return percentage;
};

const formatRows = (val) => {
  let result = val;
  if (val === undefined || val === null) {
    result = 'N/A';
  } else if (val === true) {
    result = 'Yes';
  } else if (val === false) {
    result = 'No';
  }
  return result;
};

</script>

<style scoped>
.q-table__grid-content.row {
  flex-wrap: nowrap;
}
</style>
